/* eslint-disable @typescript-eslint/no-unused-vars */
import { useEffect, useState } from 'react';
import { Alert, Avatar, Box, Grid, LinearProgress, Paper, Snackbar, SnackbarCloseReason, Typography } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import * as yup from 'yup';
import { jwtDecode } from 'jwt-decode';
import { VTextField, VForm, useVForm, IVFormErrors, VSwitch, VTextFieldPassword, VTextFieldNumber, AutoCompleteContaBancaria, AutoCompleteContaBancaria2 } from '../../../shared/forms';
import { FerramentasDeDetalhe, TelaBloqueioSalvando } from '../../../shared/components';
import { useBloqueioSalvamento } from '../../../shared/hooks';
import { LayoutBaseDePagina } from '../../../shared/layouts';
import { UsuarioService } from '../../../shared/services/api/cadastros/UsuariosService';
import NavTabsUsuario from './NavTabsUsuario';
import { Environment } from '../../../shared/environment';


interface IFormData {
  nome: string;
  login: string;
  senha: string;
  idempresa: string;
  idconta: string
  status: boolean;
  urlimg: string;
  vendedor: boolean;
  nivelacesso: number;
  comissao: number;

}
interface IFormDataUpdate {
  nome: string;
  status: boolean;
  vendedor: boolean;
  nivelacesso: number;
  comissao: number;
}

const formValidationSchema: yup.ObjectSchema<IFormData> = yup.object().shape({
  nome: yup.string().required().min(5),
  login: yup.string().required().min(5),
  senha: yup.string()
    .required('Senha é obrigatória')
    .min(8, 'A senha deve ter pelo menos 8 caracteres')
    .matches(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]+$/,
      'A senha deve conter pelo menos uma letra maiúscula, uma letra minúscula, um número e um caractere especial'
    ),
  urlimg: yup.string().optional().default(''),
  status: yup.boolean().optional().default(true),
  vendedor: yup.boolean().optional().default(false),
  idconta: yup.string().required(),
  idempresa: yup.string().required(),
  nivelacesso: yup.number().required(),
  comissao: yup.number().min(0).optional().default(0),
});

const formValidationSchema1: yup.ObjectSchema<IFormDataUpdate> = yup.object().shape({
  nome: yup.string().required(),
  status: yup.boolean().optional().default(true),
  vendedor: yup.boolean().optional().default(true),
  nivelacesso: yup.number().required(),
  comissao: yup.number().min(0).optional().default(0),
});

export const DetalheUsuario: React.FC = () => {
  const navigate = useNavigate();
  const { id = 'novo' } = useParams<'id'>();
  const { formRef, saveAndClose, save, isSaveAndClose } = useVForm();
  const [isLoading, setIsLoading] = useState(false);
  const { isSaving, progress: saveProgress, iniciar: iniciarSalvamento, concluir: concluirSalvamento, cancelar: cancelarSalvamento } = useBloqueioSalvamento();
  const [nome, setNome] = useState('');
  const [idempresa, setIdempresa] = useState('');
  const [urlimg, setUrlimg] = useState<string | null>(null);
  const [image, setImage] = useState<string | null>(null);
  const [openmsg, setOpenmsg] = useState(false);
  const [msg, setMsg] = useState('');
  const [tms, setTms] = useState<'warning' | 'error' | 'info' | 'success'>('warning');


  interface JwtPayload {
    idempresa: string;
  }
  const handleCloseMsg = (
    event?: React.SyntheticEvent | Event,
    reason?: SnackbarCloseReason,
  ) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpenmsg(false);
    if (tms === 'success') {
      navigate('/usuarios');
    }
  };

  useEffect(() => {
    const accessToken = localStorage.getItem('APP_ACCESS_TOKEN');
    if (accessToken) {
      const decoded = jwtDecode<JwtPayload>(accessToken);
      setIdempresa(decoded.idempresa);
    }
  }, []);

  useEffect(() => {
    if (id !== 'novo') {
      setIsLoading(true);
      UsuarioService.getById(id)
        .then((result) => {
          setIsLoading(false);
          if (result instanceof Error) {
            setTms('error');
            setMsg(result.message);
            setOpenmsg(true);
          } else {
            setNome(result.nome);
            formRef.current?.setData(result);
            if (result.urlimg) {
              setUrlimg(result.urlimg);
              setImage(Environment.URL_IMGUSUARIOS + result.urlimg);
            }
          }
        });
    } else {
      formRef.current?.setData({
        nome: '',
        login: '',
        senha: '',
        idempresa: '',
        comissao: 0,
        status: true,
        idconta: '',
        vendedor: false,
        nivelacesso: 1,

      });
    }
  }, [id]);

  const handleSave = (dados: IFormData) => {
    if (!iniciarSalvamento()) return;

    if (id === 'novo') {
      const dadosComIdEmpresa = {
        ...dados,
        idempresa: idempresa,
      };

      formValidationSchema
        .validate(dadosComIdEmpresa, { abortEarly: false })
        .then((dadosValidados) => {
          setIsLoading(true);
          UsuarioService
            .create(dadosValidados)
            .then((result) => {
              setIsLoading(false);
              if (result instanceof Error) {
                cancelarSalvamento();
                setTms('error');
                setMsg(result.message);
                setOpenmsg(true);
              } else {
                setTms('success');
                setMsg('Lançamento Realizado com sucesso!');
                setOpenmsg(true);
                concluirSalvamento();
              }
            });
        })
        .catch((errors: yup.ValidationError) => {
          cancelarSalvamento();
          const validationErrors: IVFormErrors = {};
          errors.inner.forEach(error => {
            if (!error.path) return;
            validationErrors[error.path] = error.message;
          });
          formRef.current?.setErrors(validationErrors);
        });

    } else {
      const updatedados = {
        nome: dados.nome,
        status: dados.status,
        vendedor: dados.vendedor,
        idconta: dados.idconta,

        nivelacesso: dados.nivelacesso,
        comissao: dados.comissao,
      };
      formValidationSchema1
        .validate(updatedados, { abortEarly: false })
        .then((dadosValidados) => {
          setIsLoading(true);
          UsuarioService
            .updateById(id, dadosValidados)
            .then((result) => {
              setIsLoading(false);
              if (result instanceof Error) {
                cancelarSalvamento();
                setTms('error');
                setMsg(result.message);
                setOpenmsg(true);
              } else {
                setTms('success');
                setMsg('Lançamento Realizado com sucesso!');
                setOpenmsg(true);
                concluirSalvamento();
              }
            });
        })
        .catch((errors: yup.ValidationError) => {
          cancelarSalvamento();
          const validationErrors: IVFormErrors = {};
          errors.inner.forEach(error => {
            if (!error.path) return;
            validationErrors[error.path] = error.message;
            setTms('error');
            setMsg(error.message);
            setOpenmsg(true);
          });
          formRef.current?.setErrors(validationErrors);
        });
    }
  };


  return (
    <>
      <TelaBloqueioSalvando
        open={isSaving}
        message={id === 'novo' ? 'Salvando usuário...' : 'Salvando alterações...'}
        progress={saveProgress}
      />
      <LayoutBaseDePagina
      titulo={nome}
      barraDeFerramentas={
        <FerramentasDeDetalhe
          mostrarBotaoSalvarEFechar={false}
          mostrarBotaoImpressao={false}
          mostrarBotaoSalvar={true}
          mostrarBotaoSalvarCarregando={isLoading}
          mostrarBotaoSalvarEFecharCarregando={isLoading}
          mostrarBotaoNovo={false}
          mostrarBotaoApagar={false}
          disableBotaoSalvar={isLoading || isSaving}
          aoClicarEmSalvar={save}
          aoClicarEmSalvarEFechar={saveAndClose}
          mostrarBotaoApagarCarregando={isLoading}
          aoClicarEmVoltar={() => navigate('/usuarios')}
          mostrarBotaoVoltarCarregando={isLoading}
        />
      }
    >
      <VForm ref={formRef} onSubmit={handleSave} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
        <Snackbar anchorOrigin={{ vertical: 'top', horizontal: 'center' }} open={openmsg} autoHideDuration={900} onClose={handleCloseMsg}>
          <Alert
            onClose={handleCloseMsg}
            severity={tms}
            variant="filled"
            sx={{ width: '100%' }}
          >
            {msg}
          </Alert>
        </Snackbar>
        <Box margin={1} display="flex" alignItems='center' flexDirection="column" component={Paper} variant="outlined">
          <Grid container direction="column" padding={1} spacing={1}>
            {isLoading && (
              <Grid item>
                <LinearProgress variant='indeterminate' />
              </Grid>
            )}

            {(id != 'novo') && (
              <NavTabsUsuario
                id={id}
                nome={nome}
                case={0}
              />
            )}
            <Grid item>
              <Typography variant='h6'>Dados do Cadastro</Typography>
            </Grid>

            <Grid container item direction="row" spacing={2}>
              <Grid item xs={5} sm={5} md={3} lg={2} xl={1.5} >
                <Box width="100%" marginRight={5} >
                  <Avatar
                    variant='rounded'
                    alt='F'
                    src={image || undefined}
                    sx={{ width: '100%', height: 200 }}
                  />
                </Box>
              </Grid>
              <Grid item xs={12} sm={12} md={9} lg={6} xl={6}>
                <Grid container item direction="row" spacing={2}>
                  <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                    <VTextField
                      fullWidth
                      size='small'
                      name='nome'
                      label='Nome'
                      onChange={e => setNome(e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
                    <VTextField
                      fullWidth
                      size='small'
                      name='login'
                      label='Login'
                      disabled={id === 'novo' ? false : true || isLoading}
                    />
                  </Grid>
                  {(id === 'novo') && (
                    <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
                      <VTextFieldPassword
                        fullWidth
                        size='small'
                        name='senha'
                        label='Senha'
                        isDisabled={isLoading}
                      />
                    </Grid>
                  )}
                  <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
                    <AutoCompleteContaBancaria2
                      name='idconta'
                      label='Conta Caixa'
                      isExternalLoading={status ? true : isLoading}
                    />
                  </Grid>
                  <Grid item xs={6} sm={6} md={6} lg={6} xl={6}>
                    <VTextFieldNumber
                      fullWidth
                      size='small'
                      name='nivelacesso'
                      label='Nivel Acesso'
                      disabled={isLoading}
                    />
                  </Grid>
                  <Grid item xs={6} sm={6} md={6} lg={6} xl={6}>
                    <VTextFieldNumber
                      fullWidth
                      size='small'
                      name='comissao'
                      label='Comissão (%)'
                      disabled={isLoading}
                    />
                  </Grid>

                  {(id != 'novo') && (
                    <Grid item xs={6} sm={6} md={3} lg={3} xl={3}>
                      <VSwitch
                        size='small'
                        name='status'
                        label='Status'
                        fdisabled={isLoading}
                        aliamento='start'
                      />
                    </Grid>

                  )}
                  <Grid item xs={6} sm={6} md={3} lg={3} xl={3}>
                    <VSwitch
                      size='small'
                      name='vendedor'
                      label='Vendedor'
                      fdisabled={isLoading}
                      aliamento='start'
                    />
                  </Grid>
                  





                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Box>
      </VForm>
      </LayoutBaseDePagina>
    </>
  );
};